/**
 * Power-up jump distances and where they land you.
 *
 * Pure logic: no Phaser imports. The generator already decided each bonus
 * platform's distance from its word difficulty (spring +2, jetpack +3/+4);
 * this module turns that into a landing index that can never overshoot the goal
 * (CLAUDE.md).
 */
import type { PlatformKind, PlatformSpec } from '../types';

/** A spring is always two platforms. */
export const SPRING_DISTANCE = 2;

/** A jetpack is three, or four for a word in the top half of the hard band. */
export const JETPACK_NEAR = 3;
export const JETPACK_FAR = 4;

/** A normal platform is always a single hop. */
export const NORMAL_DISTANCE = 1;

export type BonusKind = 'spring' | 'jetpack';

export function isBonusKind(kind: PlatformKind): kind is BonusKind {
  return kind === 'spring' || kind === 'jetpack';
}

/**
 * Distance for a bonus word. `topHalf` means the word sat in the upper half of
 * its difficulty class, which earns the longer launch.
 */
export function bonusDistanceFor(kind: BonusKind, topHalf: boolean): number {
  if (kind === 'spring') return SPRING_DISTANCE;
  return topHalf ? JETPACK_FAR : JETPACK_NEAR;
}

/** How far clearing `platform` moves you, falling back to a single hop. */
export function distanceFor(platform: PlatformSpec): number {
  if (!isBonusKind(platform.kind)) return NORMAL_DISTANCE;
  const distance = platform.bonusDistance;
  if (distance === undefined || !Number.isFinite(distance)) return NORMAL_DISTANCE;
  return Math.max(NORMAL_DISTANCE, Math.round(distance));
}

/**
 * The platform as it counts once typed: a spring or jetpack whose word had a
 * slip in it has lost its power-up and is an ordinary one-platform hop. The
 * boost is for typing the word cleanly, not for getting there eventually.
 */
export function effectivePlatform(platform: PlatformSpec, slipped: boolean): PlatformSpec {
  return slipped && isBonusKind(platform.kind) ? { ...platform, kind: 'normal' } : platform;
}

export interface LandingResult {
  /** Platform index the animal ends on. Never past the goal, never below 0. */
  readonly landing: number;
  /** Platforms actually gained, after capping. */
  readonly distance: number;
  /** True when the launch would have overshot the goal and was clamped. */
  readonly capped: boolean;
}

/**
 * Where a correct word puts the animal. The cap means a jetpack near the top
 * lands exactly on the goal rather than flying past it.
 */
export function resolveLanding(
  currentIndex: number,
  platform: PlatformSpec,
  platformCount: number,
): LandingResult {
  const top = Math.max(0, platformCount - 1);
  const from = Math.min(top, Math.max(0, Math.round(currentIndex)));
  const requested = from + distanceFor(platform);
  const landing = Math.min(top, requested);
  return { landing, distance: landing - from, capped: requested > top };
}

/** Where a wrong word puts the animal: one platform down, never below the start. */
export function resolveDrop(currentIndex: number): number {
  return Math.max(0, Math.round(currentIndex) - 1);
}

/**
 * Bonus platforms flown straight past. They are simply skipped with no penalty
 * (CLAUDE.md) - this exists so that is a tested property rather than an accident.
 */
export function skippedBonuses(
  platforms: readonly PlatformSpec[],
  from: number,
  landing: number,
): number[] {
  const skipped: number[] = [];
  for (let index = from + 1; index < landing; index += 1) {
    const platform = platforms[index];
    if (platform !== undefined && isBonusKind(platform.kind)) skipped.push(index);
  }
  return skipped;
}
